import Icon from "~/components/shared/Icon";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardPortal,
} from "~/components/shared/HoverCard";
import Badge from "~/components/shared/Badge";
import linuxLogo from "~/images/compute/machine-type-linux.png";
import macosLogo from "~/images/compute/machine-type-macos.png";
import windowsLogo from "~/images/compute/machine-type-windows.png";
import { CommandJob, OS } from "~/components/build/Show/lib/types/CommandJob";

const MACHINE_TYPES: Record<
  Exclude<OS, "unknown">,
  { value: string; label: string; logo: string }
> = {
  linux: {
    value: "linux",
    label: "Linux",
    logo: linuxLogo,
  },
  macos: {
    value: "macos",
    label: "MacOS",
    logo: macosLogo,
  },
  windows: {
    value: "windows",
    label: "Windows",
    logo: windowsLogo,
  },
};

export const AgentIcon = ({ os }: { os?: OS }) => {
  if (os && os in MACHINE_TYPES) {
    const machine = MACHINE_TYPES[os as keyof typeof MACHINE_TYPES];
    return (
      <img
        src={machine.logo}
        width="14"
        height="14"
        style={{ verticalAlign: "text-top" }}
        alt={machine.label}
      />
    );
  }

  return (
    <Icon icon="custom/outline/14x/agent" className="h-3.5 w-3.5 flex-shrink-0 text-gray-800" />
  );
};

export default function Agent({
  agentName,
  agentPath,
  queueName,
  agentsPath,
  onNestedLinkTrigger,
  hosted,
  instanceShape,
  hostedOSVersion,
}: {
  agentName: string;
  agentPath: string;
  queueName?: string;
  agentsPath: string;
  onNestedLinkTrigger: (e: React.MouseEvent | React.KeyboardEvent) => void;

  hosted?: CommandJob["hosted"];
  instanceShape?: CommandJob["instanceShape"];
  hostedOSVersion?: CommandJob["hostedOSVersion"];
}) {
  const agentLink = (
    <a href={agentPath} className="max-w-full truncate text-inherit hover:text-inherit">
      {agentName}
    </a>
  );

  const showHoverCard = !window.Features.BuildsShowAgentNameOnJobRow || queueName;

  let name;
  if (hosted) {
    name = "Hosted";
  } else if (window.Features.BuildsShowAgentNameOnJobRow) {
    name = agentName;
  } else {
    name = "Agent";
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild={true}>
        <a
          href={agentPath}
          className="job-stats flex min-w-0 flex-auto items-center gap-x-1"
          onClick={onNestedLinkTrigger}
          onKeyDown={onNestedLinkTrigger}
        >
          <Badge className="badge badge--outline gapx0.5 flex min-w-[70px] max-w-[240px] items-center justify-center">
            <AgentIcon os={instanceShape?.operatingSystem} />
            {name}
          </Badge>
        </a>
      </HoverCardTrigger>

      {showHoverCard && (
        <HoverCardPortal>
          <HoverCardContent
            className="z-30"
            size="max-lg"
            onClick={onNestedLinkTrigger}
            onKeyDown={onNestedLinkTrigger}
          >
            {queueName ? (
              <div className="grid grid-cols-[1fr_2fr] gap-x-2.5 gap-y-1.5">
                <span className="font-semibold">Agent</span>

                {agentLink}

                <span className="font-semibold">Queue</span>

                <a
                  href={agentsPath}
                  className="max-w-full truncate text-inherit hover:text-inherit"
                >
                  {queueName}
                </a>
                {hosted && instanceShape && (
                  <>
                    <span className="font-semibold">OS</span>
                    <div>
                      <Badge className="charcoal-300 bg-gray-100">
                        <img
                          src={MACHINE_TYPES[instanceShape?.operatingSystem].logo}
                          width="14"
                          height="14"
                          style={{ verticalAlign: "text-top" }}
                        />
                        {MACHINE_TYPES[instanceShape?.operatingSystem].label} {hostedOSVersion}
                      </Badge>
                    </div>
                    <span className="font-semibold">Architecture</span>
                    <div>
                      <Badge className="charcoal-300 bg-gray-100">
                        {instanceShape.architecture}
                      </Badge>
                    </div>
                    <span className="font-semibold">System</span>
                    <div>
                      <Badge className="charcoal-300 bg-gray-100">
                        {instanceShape.cpu} vCPU · {instanceShape.memory} GB RAM
                      </Badge>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex">{agentLink}</div>
            )}
          </HoverCardContent>
        </HoverCardPortal>
      )}
    </HoverCard>
  );
}
